import type { ReactNode } from 'react';
import {
  APPOINTMENT_OUTCOMES,
  APPOINTMENT_OUTCOME_COLORS,
  APPOINTMENT_OUTCOME_LABELS,
  type AppointmentFilterState,
  type AppointmentOutcome,
  type LocationSummary,
} from '../types';
import { daysAgo, toDateInput } from '../format';
import { TagFilter, type TagOption } from './TagFilter';
import { DateRangeField, FilterPanel, SwitchField } from './FilterPanel';

interface Person {
  userId: string | null;
  userName: string | null;
  locationId: string;
  total: number;
}

interface Props {
  filters: AppointmentFilterState;
  onChange: (next: AppointmentFilterState) => void;
  onReset?: () => void;
  /** The saved-views menu, shown in the panel header. */
  views?: ReactNode;
  locations: LocationSummary[];
  people: Person[];
  calendars: { id: string; name: string | null; locationId: string }[];
  tagOptions?: TagOption[];
  showSearch?: boolean;
}

/**
 * Appointments have a forward-looking range as well as a backward one, because
 * they are booked into the future — so the presets differ from the call log's.
 */
const PRESETS: { label: string; from: number; to: number }[] = [
  // Matches the page's default range, so the active preset is visible on load.
  { label: '±30d', from: 29, to: 30 },
  { label: 'Next 30d', from: 0, to: 30 },
  { label: 'Last 30d', from: 29, to: 0 },
  { label: 'Last 90d', from: 89, to: 0 },
];

function daysAhead(days: number): string {
  return toDateInput(new Date(Date.now() + days * 86_400_000));
}

export function AppointmentFilterBar({
  filters,
  onChange,
  onReset,
  views,
  locations,
  people,
  calendars,
  tagOptions = [],
  showSearch,
}: Props) {
  const dateOf = (offset: number, ahead: boolean) =>
    offset === 0 ? toDateInput(new Date()) : ahead ? daysAhead(offset) : daysAgo(offset);

  function toggleOutcome(outcome: AppointmentOutcome) {
    const next = filters.outcomes.includes(outcome)
      ? filters.outcomes.filter((item) => item !== outcome)
      : [...filters.outcomes, outcome];
    onChange({ ...filters, outcomes: next });
  }

  // When the toggle is on, the picker offers only the sub-accounts the figures
  // are drawn from, so the list and the numbers never disagree.
  const visibleLocations = filters.activeClientsOnly
    ? locations.filter((location) => location.isActiveClient)
    : locations;

  const activeCount =
    (filters.locationIds.length ? 1 : 0) +
    (filters.userIds.length ? 1 : 0) +
    (filters.calendarIds.length ? 1 : 0) +
    (filters.outcomes.length ? 1 : 0) +
    (filters.tags.length ? 1 : 0) +
    (showSearch && filters.search.trim() ? 1 : 0) +
    (filters.activeClientsOnly ? 0 : 1);

  return (
    <FilterPanel actions={views} activeCount={activeCount} onReset={onReset}>
      <div className="field">
        <label>Count by</label>
        <div className="segmented" role="group" aria-label="Count by">
          {(
            [
              ['start', 'Appointment date'],
              ['booked', 'Date booked'],
            ] as [AppointmentFilterState['dateBasis'], string][]
          ).map(([value, label]) => (
            <button
              type="button"
              key={value}
              aria-pressed={filters.dateBasis === value}
              onClick={() => onChange({ ...filters, dateBasis: value })}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <DateRangeField
        id="appt-from"
        label="Date range"
        from={filters.from}
        to={filters.to}
        presets={PRESETS.map((preset) => ({
          label: preset.label,
          from: dateOf(preset.from, false),
          to: dateOf(preset.to, true),
        }))}
        onChange={(from, to) => onChange({ ...filters, from, to })}
      />

      <SwitchField
        id="appt-active"
        label="Clients"
        text="Active only"
        checked={filters.activeClientsOnly}
        onChange={(checked) => onChange({ ...filters, activeClientsOnly: checked, locationIds: [] })}
      />

      <div className="field">
        <label htmlFor="appt-location">Sub-account</label>
        <select
          id="appt-location"
          value={filters.locationIds[0] ?? ''}
          onChange={(event) =>
            onChange({
              ...filters,
              locationIds: event.target.value ? [event.target.value] : [],
              userIds: [],
              calendarIds: [],
            })
          }
        >
          <option value="">
            {filters.activeClientsOnly ? 'All active clients' : 'All sub-accounts'}
          </option>
          {visibleLocations.map((location) => (
            <option value={location.id} key={location.id}>
              {location.name}
            </option>
          ))}
        </select>
      </div>

      <div className="field">
        <label htmlFor="appt-user">Person</label>
        <div className="input-group">
          <select
            aria-label="Person basis"
            value={filters.userBasis}
            onChange={(event) =>
              onChange({
                ...filters,
                userBasis: event.target.value as AppointmentFilterState['userBasis'],
                userIds: [],
              })
            }
          >
            <option value="booker">Booked by</option>
            <option value="assigned">Assigned to</option>
          </select>
          <select
            id="appt-user"
            value={filters.userIds[0] ?? ''}
            onChange={(event) => onChange({ ...filters, userIds: event.target.value ? [event.target.value] : [] })}
          >
            <option value="">Anyone</option>
            <option value="unassigned">
              {filters.userBasis === 'assigned' ? 'Unassigned' : 'Self-booked / automated'}
            </option>
            {people
              .filter((person) => person.userId)
              .map((person) => (
                <option value={person.userId!} key={`${person.userId}-${person.locationId}`}>
                  {person.userName ?? person.userId} ({person.total})
                </option>
              ))}
          </select>
        </div>
      </div>
      {calendars.length > 0 ? (
        <div className="field">
          <label htmlFor="appt-calendar">Calendar</label>
          <select
            id="appt-calendar"
            value={filters.calendarIds[0] ?? ''}
            onChange={(event) =>
              onChange({ ...filters, calendarIds: event.target.value ? [event.target.value] : [] })
            }
          >
            <option value="">All calendars</option>
            {calendars.map((calendar) => (
              <option value={calendar.id} key={calendar.id}>
                {calendar.name ?? calendar.id}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      <div className="field">
        <label>Status</label>
        <div className="chips">
          {APPOINTMENT_OUTCOMES.map((outcome) => (
            <button
              type="button"
              className="chip"
              key={outcome}
              aria-pressed={filters.outcomes.includes(outcome)}
              onClick={() => toggleOutcome(outcome)}
            >
              <span
                className="swatch"
                style={{ background: APPOINTMENT_OUTCOME_COLORS[outcome] }}
                aria-hidden="true"
              />
              {APPOINTMENT_OUTCOME_LABELS[outcome]}
            </button>
          ))}
        </div>
      </div>

      <TagFilter
        id="appt-tags"
        options={tagOptions}
        selected={filters.tags}
        match={filters.tagMatch}
        onChange={(tags, tagMatch) => onChange({ ...filters, tags, tagMatch })}
      />

      {showSearch ? (
        <div className="field field-grow">
          <label htmlFor="appt-search">Search</label>
          <input
            id="appt-search"
            type="search"
            placeholder="Contact, title or booker…"
            value={filters.search}
            onChange={(event) => onChange({ ...filters, search: event.target.value })}
          />
        </div>
      ) : null}
    </FilterPanel>
  );
}
